import { formatAmountInWords } from '../../utils/amountInWords';

export { formatAmountInWords };

const PRINT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const hasPrintValue = (value) => {
    if (value === undefined || value === null) return false;
    if (typeof value === 'string') return value.trim() !== '';
    return true;
};

export const getText = (value, preferredLang = 'en') => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object') {
        if (value[preferredLang]) return value[preferredLang];
        if (value.label) return typeof value.label === 'string' ? value.label : getText(value.label, preferredLang);
        const first = Object.values(value)[0];
        return typeof first === 'string' ? first : '';
    }
    return String(value);
};

export const toPrintUpper = (text) => {
    if (!hasPrintValue(text)) return '';
    return String(text).toUpperCase();
};

export const fieldIfValue = (label, value) => {
    const normalized = typeof value === 'string' ? value.trim() : value;
    if (!hasPrintValue(normalized)) return null;
    return { label, value: normalized };
};

export const formatPrintDate = (dateString) => {
    if (!hasPrintValue(dateString)) return null;
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return null;
    const day = date.getDate().toString().padStart(2, '0');
    const month = PRINT_MONTHS[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
};

/** Screen / legacy date display */
export const formatDate = (dateString) => {
    const formatted = formatPrintDate(dateString);
    return formatted || 'N/A';
};

export const formatPrintQty = (qty) => {
    if (qty === undefined || qty === null || qty === '') return '';
    const num = parseFloat(qty);
    if (Number.isNaN(num)) return String(qty);
    if (Number.isInteger(num) || num === Math.trunc(num)) return String(Math.trunc(num));
    return String(num);
};

export const formatPrintAmount = (amount) => {
    if (amount === undefined || amount === null || amount === '') return '0.000';
    const num = parseFloat(amount);
    if (Number.isNaN(num)) return '0.000';
    return num.toFixed(3);
};

export const formatCurrency = (amount, decimals = 3) => formatPrintAmount(amount);

export const getItemPrintDescription = (row) => {
    const desc = getText(row?.item_description);
    return hasPrintValue(desc) ? toPrintUpper(desc) : '';
};

export const getCountryLabel = (data) => {
    return getText(data?.country?.label) || getText(data?.country?.name) || '';
};

const STRUCTURED_ADDRESS_KEYS = ['building', 'road', 'flat', 'block', 'area'];
const STRUCTURED_ADDRESS_LINE1 = ['building', 'road', 'flat'];
const STRUCTURED_ADDRESS_LINE2 = ['block', 'area'];
const STRUCTURED_ADDRESS_REGEX = /\b(building|road|flat|block|area)\s*:/i;

const parseStructuredAddressString = (text) => {
    const parts = {};
    if (!hasPrintValue(text)) return parts;
    String(text).split(',').forEach((segment) => {
        const trimmed = segment.trim();
        const match = trimmed.match(/^([a-z]+)\s*:\s*(.*)$/i);
        if (match) {
            parts[match[1].toLowerCase()] = match[2].trim();
        }
    });
    return parts;
};

const isStructuredAddressText = (text) => STRUCTURED_ADDRESS_REGEX.test(String(text || ''));

const getStructuredAddressParts = (data) => {
    const address1Text = getText(data?.address1);
    const address2Text = getText(data?.address2);
    const parts = {
        ...parseStructuredAddressString(address1Text),
        ...parseStructuredAddressString(address2Text),
    };

    STRUCTURED_ADDRESS_KEYS.forEach((key) => {
        const direct = data?.[key] ?? data?.customer?.[key];
        if (hasPrintValue(direct)) {
            parts[key] = String(direct).trim();
        }
    });

    return { parts, address1Text, address2Text };
};

const formatStructuredAddressLine = (parts, keys) => keys
    .map((key) => {
        const value = parts[key];
        const display = hasPrintValue(value) ? value : '-';
        return `${key.toUpperCase()}: ${display}`;
    })
    .join(', ');

const shouldShowStructuredAddress = (data, parts, address1Text, address2Text) => (
    isStructuredAddressText(address1Text)
    || isStructuredAddressText(address2Text)
    || STRUCTURED_ADDRESS_KEYS.some((key) => hasPrintValue(parts[key]))
    || (data?.address2 !== undefined && data?.address2 !== null && data?.address2 !== '')
);

export const getCustomerPrintLines = (data) => {
    const lines = [];
    const name = getText(data?.customer_name);
    if (hasPrintValue(name)) lines.push({ kind: 'text', text: toPrintUpper(name) });

    const { parts, address1Text, address2Text } = getStructuredAddressParts(data);
    const showStructured = shouldShowStructuredAddress(data, parts, address1Text, address2Text);

    if (hasPrintValue(address1Text) && !isStructuredAddressText(address1Text)) {
        lines.push({ kind: 'text', text: toPrintUpper(address1Text) });
    }

    if (showStructured) {
        lines.push({
            kind: 'text',
            text: toPrintUpper(formatStructuredAddressLine(parts, STRUCTURED_ADDRESS_LINE1)),
        });
        lines.push({
            kind: 'text',
            text: toPrintUpper(formatStructuredAddressLine(parts, STRUCTURED_ADDRESS_LINE2)),
        });
    } else if (hasPrintValue(address2Text) && !isStructuredAddressText(address2Text)) {
        lines.push({ kind: 'text', text: toPrintUpper(address2Text) });
    }

    const country = getCountryLabel(data);
    if (hasPrintValue(country)) lines.push({ kind: 'text', text: toPrintUpper(country) });

    const phone = getText(data?.phone_number) || getText(data?.customer?.phone);
    lines.push({
        kind: 'labeled',
        label: 'PHONE:',
        value: hasPrintValue(phone) ? phone : '-',
    });

    const vat = getText(data?.vat_number) || getText(data?.customer?.vat_number);
    lines.push({
        kind: 'labeled',
        label: 'VAT NO:',
        value: hasPrintValue(vat) ? vat : '-',
    });

    return lines;
};

export const getDeliveryNoteList = (data) => {
    if (!Array.isArray(data?.linked_delivery_notes)) return [];
    return data.linked_delivery_notes
        .map((dn) => dn?.label || dn?.invoice_number || getText(dn))
        .filter((v) => hasPrintValue(v));
};

export const getDeliveryNoteRefLine = (data) => {
    const notes = getDeliveryNoteList(data);
    if (notes.length === 0) return null;
    return notes.map((n) => toPrintUpper(n)).join(', ');
};

export const formatPrintAmountInWords = (amount) => {
    const words = formatAmountInWords(amount);
    if (!hasPrintValue(words)) return '';
    return `BD. ${words}`;
};

export const getDeliveryNoteGroupedItems = (data, getTextFn = getText) => {
    if (Array.isArray(data?.grouped_items) && data.grouped_items.length > 0) {
        return data.grouped_items;
    }

    const lineItems = Array.isArray(data?.items)
        ? data.items
        : (Array.isArray(data?.delivery_note_items) ? data.delivery_note_items : []);

    const groupedMap = new Map();
    lineItems.forEach((item) => {
        const key = item?.job_order_id || item?.job_order_number || 'unknown';
        if (!groupedMap.has(key)) {
            groupedMap.set(key, {
                job_order_id: item?.job_order_id || null,
                job_order_number: getTextFn(item?.job_order_number) || '',
                employee_name: getTextFn(item?.employee_name) || '',
                items: [],
            });
        }
        groupedMap.get(key).items.push(item);
    });

    return Array.from(groupedMap.values()).map((group, index) => ({
        ...group,
        serial_number: index + 1,
    }));
};

export const getTaxInvoiceLineItems = (data, documentType) => {
    if (documentType === 'saleInvoice') {
        return Array.isArray(data.grouped_items)
            ? data.grouped_items
            : (Array.isArray(data.sale_invoice_items) ? data.sale_invoice_items : []);
    }
    return Array.isArray(data.items) ? data.items : [];
};

export const computeAmountExclVat = (row) => {
    if (row.amount_excl_vat != null) return row.amount_excl_vat;
    if (row.amount_excluding_vat != null) return row.amount_excluding_vat;
    if (row.line_amount != null) return row.line_amount;
    const qty = row.quantity || row.qty || 0;
    const unitPrice = parseFloat(row.unit_price || 0);
    const discountPercent = parseFloat(row.discount_percentage || 0);
    const lineTotal = qty * unitPrice;
    const discountAmount = (lineTotal * discountPercent) / 100;
    return lineTotal - discountAmount;
};
